import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

interface PageHeaderProps {
  title: string
  description?: string
  actions?: ReactNode
}

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <header className="flex flex-col sm:flex-row items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </header>
  )
}

interface ScreenHeaderProps {
  title: string
  subtitle?: string
  trailing?: ReactNode
  className?: string
}

/**
 * Mobile screen title, for full-screen diner flows. Desktop pages keep using
 * PageHeader.
 *
 * **It carries no back affordance, and there is no prop to ask for one.** The
 * diner shell already answers "how do I get out of here": the tab bar inside a
 * visit, the avatar and the profile hub outside one. A second, differently-placed
 * way back competes with that, and on a screen reached from the hub it points at
 * the one place the diner can already see.
 *
 * This used to be a `showBack` prop defaulting to `true`, which meant omitting it
 * was how the button appeared — and it appeared by accident on three screens
 * before review caught each one. A default nobody wants is not a default; it is a
 * trap with a workaround. Removing the prop is what makes the rule structural
 * rather than something every new screen has to remember to opt out of.
 */
export function ScreenHeader({
  title,
  subtitle,
  trailing,
  className,
}: ScreenHeaderProps) {
  return (
    <header
      className={cn(
        "flex items-center gap-2 border-b bg-background px-4 py-2.5",
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-semibold tracking-tight">{title}</h1>
        {subtitle && (
          <p className="truncate text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-1">{trailing}</div>}
    </header>
  )
}

interface SectionHeaderProps {
  title: string
  trailing?: ReactNode
  className?: string
}

/** In-page section title with an optional trailing action (link, count). */
export function SectionHeader({ title, trailing, className }: SectionHeaderProps) {
  return (
    <div className={cn("mb-2 flex items-baseline justify-between gap-2 px-4", className)}>
      <h3 className="truncate text-base font-semibold tracking-tight">{title}</h3>
      {trailing && (
        <div className="shrink-0 text-sm font-medium text-brand-ink">{trailing}</div>
      )}
    </div>
  )
}
